import { type FormEvent, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { apiError, recruiterSelfApi } from "../api";
import { dayLong, money } from "../format";
import { Field, Modal } from "../ui";
import type { ChecklistItem, PortfolioProducer, ProgramCompany, Recruiter, RecruiterEventRow, Statement } from "../types";
import { useToast } from "@/app/providers/Toaster";

function useSelfMutation<T>(fn: (v: T) => Promise<void>, message: string, onClose?: () => void) {
  const { push } = useToast();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      push({ kind: "success", message });
      qc.invalidateQueries({ queryKey: ["recruiter-self"] });
      onClose?.();
    },
    onError: (err) => push({ kind: "error", message: apiError(err) }),
  });
}

function Actions({ onClose, label, disabled, pending }: { onClose: () => void; label: string; disabled?: boolean; pending?: boolean }) {
  return (
    <>
      <button type="button" className="nx-btn-ghost" onClick={onClose}>
        Cancelar
      </button>
      <button type="submit" form="nx-self-form" className="nx-btn-primary" disabled={disabled || pending}>
        {pending && <Loader2 size={14} className="animate-spin" />}
        {label}
      </button>
    </>
  );
}

const submit = (fn: () => void) => (e: FormEvent) => {
  e.preventDefault();
  fn();
};

export function ProposeModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const mut = useSelfMutation(
    () => recruiterSelfApi.propose({ name: name.trim(), contactEmail: email.trim() || undefined, phone: phone.trim() || undefined, note: note.trim() || undefined }),
    "Propuesta enviada. Mixcore la registra y te avisa.",
    onClose,
  );
  const valid = name.trim().length >= 2 && (!!email.trim() || !!phone.trim());
  return (
    <Modal title="Proponer un productor" onClose={onClose} footer={<Actions onClose={onClose} label="Enviar propuesta" disabled={!valid} pending={mut.isPending} />}>
      <form id="nx-self-form" className="flex flex-col gap-4" onSubmit={submit(() => valid && mut.mutate(undefined))}>
        <p className="text-sm text-fg-muted">
          Queda a tu nombre desde que Mixcore lo registra en el CRM. Si ya estaba en nuestro pipeline, te lo decimos de
          inmediato.
        </p>
        <Field label="Productor o productora" required>
          <input className="nx-input" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Correo">
            <input className="nx-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Field label="Teléfono">
            <input className="nx-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
        </div>
        <Field label="Cuéntanos de él" hint="Qué hace, cada cuánto, dónde, cómo lo conociste.">
          <textarea className="nx-input min-h-[80px]" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

const ACTIVITY: { value: "reunion" | "respuesta" | "avance"; label: string }[] = [
  { value: "reunion", label: "Reunión" },
  { value: "respuesta", label: "Respuesta del productor" },
  { value: "avance", label: "Avance en su incorporación" },
];

export function ActivityModal({ producer, onClose }: { producer: PortfolioProducer; onClose: () => void }) {
  const [kind, setKind] = useState<"reunion" | "respuesta" | "avance">("reunion");
  const [note, setNote] = useState("");
  const mut = useSelfMutation(() => recruiterSelfApi.logActivity(producer.id, { kind, note: note.trim() }), "Actividad registrada.", onClose);
  return (
    <Modal title={`Actividad con ${producer.name}`} onClose={onClose} footer={<Actions onClose={onClose} label="Registrar" disabled={note.trim().length < 3} pending={mut.isPending} />}>
      <form id="nx-self-form" className="flex flex-col gap-4" onSubmit={submit(() => note.trim().length >= 3 && mut.mutate(undefined))}>
        <p className="text-sm text-fg-muted">Cuenta como actividad una reunión, una respuesta del productor o un avance concreto: mantiene la asignación vigente.</p>
        <Field label="¿Qué pasó?">
          <div className="grid gap-2">
            {ACTIVITY.map((a) => (
              <label key={a.value} className="flex items-center gap-2 text-sm text-fg">
                <input type="radio" name="kind" checked={kind === a.value} onChange={() => setKind(a.value)} />
                {a.label}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Detalle" required>
          <textarea className="nx-input min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

export function ReportModal({ ev, onClose }: { ev: RecruiterEventRow; onClose: () => void }) {
  const [worked, setWorked] = useState(ev.report?.worked ?? "");
  const [failed, setFailed] = useState(ev.report?.failed ?? "");
  const [asked, setAsked] = useState(ev.report?.producerAsked ?? "");
  const valid = !!(worked.trim() || failed.trim() || asked.trim());
  const mut = useSelfMutation(
    () => recruiterSelfApi.submitReport(ev.id, { worked: worked.trim(), failed: failed.trim(), producerAsked: asked.trim() }),
    "Reporte enviado. Gracias.",
    onClose,
  );
  const due = ev.items.find((i) => i.key === "report")?.dueDate;
  return (
    <Modal title={`Reporte de ${ev.name}`} onClose={onClose} footer={<Actions onClose={onClose} label="Enviar reporte" disabled={!valid} pending={mut.isPending} />}>
      <form id="nx-self-form" className="flex flex-col gap-4" onSubmit={submit(() => valid && mut.mutate(undefined))}>
        {due && <p className="text-sm text-fg-muted">Plazo: {dayLong(due)}. Breve y concreto: lo leemos todo.</p>}
        <Field label="¿Qué funcionó?">
          <textarea className="nx-input min-h-[64px]" value={worked} onChange={(e) => setWorked(e.target.value)} autoFocus />
        </Field>
        <Field label="¿Qué falló?">
          <textarea className="nx-input min-h-[64px]" value={failed} onChange={(e) => setFailed(e.target.value)} />
        </Field>
        <Field label="¿Qué pidió el productor?">
          <textarea className="nx-input min-h-[64px]" value={asked} onChange={(e) => setAsked(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

export function ForceMajeureModal({ ev, item, onClose }: { ev: RecruiterEventRow; item: ChecklistItem; onClose: () => void }) {
  const [note, setNote] = useState("");
  const mut = useSelfMutation(
    () => recruiterSelfApi.updateItem(ev.id, item.key, { status: "na", reason: "force_majeure", note: note.trim() }),
    "Listo: queda como fuerza mayor.",
    onClose,
  );
  return (
    <Modal title={`${item.label} · fuerza mayor`} onClose={onClose} footer={<Actions onClose={onClose} label="Guardar" disabled={!note.trim()} pending={mut.isPending} />}>
      <form id="nx-self-form" className="flex flex-col gap-4" onSubmit={submit(() => note.trim() && mut.mutate(undefined))}>
        <p className="text-sm text-fg-muted">Enfermedad, emergencia o cancelación del evento por parte del productor no cuentan como incumplimiento.</p>
        <Field label="¿Qué pasó?" required>
          <textarea className="nx-input min-h-[72px]" value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
        </Field>
      </form>
    </Modal>
  );
}

export function SimultaneousModal({ ev, candidates, onClose }: { ev: RecruiterEventRow; candidates: RecruiterEventRow[]; onClose: () => void }) {
  const [otherId, setOtherId] = useState<number>(candidates[0]?.id ?? 0);
  const [attendedId, setAttendedId] = useState<number>(ev.id);
  const [note, setNote] = useState("");
  const other = candidates.find((c) => c.id === otherId);
  const mut = useSelfMutation(
    () => recruiterSelfApi.declareSimultaneous(ev.id, { otherEventId: otherId, attendedId, note: note.trim() || undefined }),
    "Aviso enviado: la presencia se da por cumplida en ambos.",
    onClose,
  );
  return (
    <Modal title="Evento simultáneo" onClose={onClose} footer={<Actions onClose={onClose} label="Avisar a Mixcore" disabled={!otherId} pending={mut.isPending} />}>
      <form id="nx-self-form" className="flex flex-col gap-4" onSubmit={submit(() => otherId && mut.mutate(undefined))}>
        <p className="text-sm text-fg-muted">
          Si dos productores de tu cartera tienen evento la misma noche en etapa de activación, eliges a cuál ir y avisas con
          72 horas de anticipación. No pierdes la comisión de ninguno.
        </p>
        {candidates.length > 1 && (
          <Field label="El otro evento">
            <select className="nx-input" value={otherId} onChange={(e) => setOtherId(Number(e.target.value))}>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.producerName}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label="¿A cuál vas a ir?">
          <div className="grid gap-2">
            {[ev, other].filter(Boolean).map((x) => (
              <label key={x!.id} className="flex items-center gap-2 text-sm text-fg">
                <input type="radio" name="attended" checked={attendedId === x!.id} onChange={() => setAttendedId(x!.id)} />
                {x!.name} · {x!.producerName}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Nota (opcional)">
          <input className="nx-input" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

export function InvoiceModal({ statement, company, onClose }: { statement: Statement; company: ProgramCompany; onClose: () => void }) {
  const [number, setNumber] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const mut = useSelfMutation(() => recruiterSelfApi.uploadInvoice(statement.id, number.trim(), file!), "Boleta recibida. Te avisamos cuando te paguemos.", onClose);
  const valid = !!number.trim() && !!file;
  return (
    <Modal title={`Boleta de ${statement.periodLabel}`} onClose={onClose} footer={<Actions onClose={onClose} label="Subir boleta" disabled={!valid} pending={mut.isPending} />}>
      <form id="nx-self-form" className="flex flex-col gap-4" onSubmit={submit(() => valid && mut.mutate(undefined))}>
        <div className="rounded-md bg-bg-subtle p-3 text-sm space-y-0.5">
          <div>
            Monto bruto: <b className="text-fg tabular-nums">{money(statement.gross)}</b>
          </div>
          <div>
            A nombre de: <b className="text-fg">{company.name}</b> · RUT {company.rut}
          </div>
          <div className="text-fg-muted">Giro: {company.giro}</div>
          <div className="text-fg-muted">Dirección: {company.address}</div>
        </div>
        <Field label="N.º de la boleta" required>
          <input className="nx-input" inputMode="numeric" value={number} onChange={(e) => setNumber(e.target.value)} autoFocus />
        </Field>
        <Field label="Archivo de la boleta" required hint="PDF, JPG o PNG de hasta 5 MB.">
          <input type="file" accept="application/pdf,image/jpeg,image/png" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        </Field>
      </form>
    </Modal>
  );
}

const ACCOUNT_TYPES = ["Cuenta corriente", "Cuenta vista", "Cuenta RUT", "Cuenta de ahorro"];

export function PaymentForm({ recruiter }: { recruiter: Recruiter }) {
  const b = recruiter.bank;
  const [bankName, setBankName] = useState(b?.bankName ?? "");
  const [accountType, setAccountType] = useState(b?.accountType ?? "");
  const [accountNumber, setAccountNumber] = useState(b?.accountNumber ?? "");
  const [holderName, setHolderName] = useState(b?.holderName ?? recruiter.name);
  const [holderRut, setHolderRut] = useState(b?.holderRut ?? recruiter.rut ?? "");
  const [phone, setPhone] = useState(recruiter.phone ?? "");
  const [rut, setRut] = useState(recruiter.rut ?? "");
  const mut = useSelfMutation(
    () =>
      recruiterSelfApi.updatePayment({
        bankName: bankName.trim(),
        accountType,
        accountNumber: accountNumber.trim(),
        holderName: holderName.trim(),
        holderRut: holderRut.trim(),
        phone: phone.trim(),
        rut: rut.trim(),
      }),
    "Datos guardados.",
  );
  return (
    <form className="flex flex-col gap-4" onSubmit={submit(() => mut.mutate(undefined))}>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Tu RUT">
          <input className="nx-input" value={rut} onChange={(e) => setRut(e.target.value)} placeholder="12.345.678-5" />
        </Field>
        <Field label="Teléfono">
          <input className="nx-input" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Banco">
          <input className="nx-input" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="Banco Estado" />
        </Field>
        <Field label="Tipo de cuenta">
          <select className="nx-input" value={accountType} onChange={(e) => setAccountType(e.target.value)}>
            <option value="">Elegir…</option>
            {ACCOUNT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="N.º de cuenta">
          <input className="nx-input" inputMode="numeric" value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
        </Field>
        <Field label="Titular">
          <input className="nx-input" value={holderName} onChange={(e) => setHolderName(e.target.value)} />
        </Field>
        <Field label="RUT del titular">
          <input className="nx-input" value={holderRut} onChange={(e) => setHolderRut(e.target.value)} />
        </Field>
      </div>
      <div className="flex justify-end">
        <button type="submit" className="nx-btn-primary" disabled={mut.isPending}>
          {mut.isPending && <Loader2 size={14} className="animate-spin" />}
          Guardar
        </button>
      </div>
    </form>
  );
}
